import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6omesl39lnhn4hh1d10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <footer id="contact" className="bg-[#1B3A2F] relative overflow-hidden">
      {/* World Map Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M10 10h80v80H10z\' fill=\'none\' stroke=\'%232A4A3A\' stroke-width=\'0.5\'/%3E%3C/svg%3E")',
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      <div className="relative px-20 py-20">
        {/* Upper Section */}
        <div className="flex gap-20 mb-20">
          {/* Left Column */}
          <div className="w-[30%]">
            <div className="flex items-center gap-3 mb-8">
              <img src="https://public.readdy.ai/ai/img_res/946c6096-d0b3-46bd-9556-bc1053c0268e.png" alt="LILA SH.P.K. Logo" className="h-8 w-8 object-contain" />
              <h5 className="text-2xl text-white font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
                LILA SH.P.K.<span className="text-cyan-400">.</span>
              </h5>
            </div>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="mb-10">
              <div className="flex gap-3 mb-6">
                <input 
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email" 
                  required
                  className="flex-1 bg-transparent border-2 border-[#6B8B7E] rounded-lg px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-cyan-400 transition-colors"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-12 h-12 flex items-center justify-center border-2 border-white rounded-full text-white hover:bg-white hover:text-[#1B3A2F] transition-all whitespace-nowrap disabled:opacity-50"
                >
                  <i className="ri-mail-line text-xl"></i>
                </button>
              </div>
              
              <input 
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your name"
                required
                className="w-full bg-transparent border-2 border-[#6B8B7E] rounded-lg px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-cyan-400 transition-colors mb-4"
              />
              
              <input 
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                required
                className="w-full bg-transparent border-2 border-[#6B8B7E] rounded-lg px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-cyan-400 transition-colors mb-4"
              />
              
              <textarea 
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Your message"
                required
                maxLength={500}
                rows={4}
                className="w-full bg-transparent border-2 border-[#6B8B7E] rounded-lg px-4 py-3 text-white placeholder-gray-400 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none"
              ></textarea>
              
              <div className="text-xs text-gray-400 mt-2 mb-4">
                {formData.message.length}/500 characters
              </div>

              {submitStatus === 'success' && (
                <div className="text-green-400 text-sm mb-4">Message sent successfully!</div>
              )}
              {submitStatus === 'error' && (
                <div className="text-red-400 text-sm mb-4">Failed to send message. Please try again.</div>
              )}
            </form>
            
            <h6 className="text-[28px] text-white leading-tight" style={{ fontFamily: 'Playfair Display, serif' }}>
              Stay Updated with<br />
              Latest iOS Solutions
            </h6>
          </div>

          {/* Right Navigation Grid */}
          <div className="flex-1 grid grid-cols-4 gap-10">
            {/* Company */}
            <div>
              <h6 className="text-base text-white font-semibold tracking-[3px] mb-5">COMPANY</h6>
              <ul className="space-y-4">
                <li><a href="#home" className="text-sm text-white hover:text-cyan-400 transition-colors">Home</a></li>
                <li><a href="#about" className="text-sm text-white hover:text-cyan-400 transition-colors">About</a></li>
                <li><a href="#products" className="text-sm text-white hover:text-cyan-400 transition-colors">Team</a></li>
              </ul>
            </div>

            {/* Products */}
            <div>
              <h6 className="text-base text-white font-semibold tracking-[3px] mb-5">PRODUCTS</h6>
              <ul className="space-y-4">
                <li><span className="text-sm text-white">VPN App</span></li>
                <li><span className="text-sm text-white">Cleaner App</span></li>
                <li><span className="text-sm text-white">Security Suite</span></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h6 className="text-base text-white font-semibold tracking-[3px] mb-5">RESOURCES</h6>
              <ul className="space-y-4">
                <li><span className="text-sm text-white">Documentation</span></li>
                <li><span className="text-sm text-white">Support</span></li>
                <li><span className="text-sm text-white">Updates</span></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h6 className="text-base text-white font-semibold tracking-[3px] mb-5">CONTACT</h6>
              <ul className="space-y-4">
                <li className="flex items-start gap-2">
                  <i className="ri-map-pin-line text-cyan-400 mt-1"></i>
                  <span className="text-sm text-white">Address: SYZES</span>
                </li>
                <li className="text-sm text-white">City: BERAT</li>
                <li className="text-sm text-white">Zip: 5000</li>
                <li className="flex items-start gap-2">
                  <i className="ri-flag-line text-cyan-400 mt-1"></i>
                  <span className="text-sm text-white">Country: Albania</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-mail-line text-cyan-400 mt-1"></i>
                  <a href="mailto:info@lilashpk.net" className="text-sm text-white hover:text-cyan-400 transition-colors">info@lilashpk.net</a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-phone-line text-cyan-400 mt-1"></i>
                  <a href="tel:+17817377801" className="text-sm text-white hover:text-cyan-400 transition-colors">+1 7817377801</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Lower Section - Copyright Bar */}
        <div className="pt-16 border-t border-[#3A5A4A]">
          <div className="flex items-center justify-between">
            <p className="text-[13px] text-white/40">
              © 2025 LILA SH.P.K. All rights reserved.
            </p>
            
            <div className="flex items-center gap-5">
              <a href="https://linkedin.com" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/50 rounded-lg hover:bg-white hover:text-[#1B3A2F] transition-all">
                <i className="ri-linkedin-line text-lg text-white"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/50 rounded-lg hover:bg-white hover:text-[#1B3A2F] transition-all">
                <i className="ri-twitter-x-line text-lg text-white"></i>
              </a>
              <a href="https://facebook.com" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/50 rounded-lg hover:bg-white hover:text-[#1B3A2F] transition-all">
                <i className="ri-facebook-line text-lg text-white"></i>
              </a>
              <a href="https://instagram.com" target="_blank" rel="nofollow noopener noreferrer" className="w-9 h-9 flex items-center justify-center border border-white/50 rounded-lg hover:bg-white hover:text-[#1B3A2F] transition-all">
                <i className="ri-instagram-line text-lg text-white"></i>
              </a>
            </div>
            
            <p className="text-[13px] text-white/40">
              Privacy Policy
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}